/**
 * Quiz Game
 * Answer trivia questions correctly to earn points
 */

import React, { useState, useEffect } from 'react';
import { useLocalStorage } from './hooks/useLocalStorage';
import GameLayout from './common/GameLayout';
import ScoreDisplay from './common/ScoreDisplay';
import { GAME_CONFIG } from './config/gameConfig';

const QuizGame = ({ onHome }) => {
  // Game State
  const [gameState, setGameState] = useState(GAME_CONFIG.states.PLAYING);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useLocalStorage('quizGame_score', 0);
  const [highScore, setHighScore] = useLocalStorage('quizGame_highScore', 0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Decode HTML entities and URL encoding
  const decodeHtml = (html) => {
    if (!html) return '';
    // First decode URL encoding
    let decoded = decodeURIComponent(html);
    // Then decode HTML entities
    const textarea = document.createElement('textarea');
    textarea.innerHTML = decoded;
    return textarea.value;
  };

  // Shuffle array
  const shuffleArray = (arr) => {
    return [...arr].sort(() => Math.random() - 0.5);
  };

  // Fetch questions from Open Trivia Database
  const fetchQuestions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(
        'https://opentdb.com/api.php?amount=10&type=multiple&difficulty=medium'
      );
      const data = await response.json();

      if (data.response_code !== 0) {
        throw new Error('Failed to fetch questions');
      }

      // Transform API data to game format
      const formattedQuestions = data.results.map((q) => {
        const allAnswers = [
          decodeHtml(q.correct_answer),
          ...q.incorrect_answers.map((a) => decodeHtml(a))
        ];
        const shuffledOptions = shuffleArray(allAnswers);

        return {
          q: decodeHtml(q.question),
          a: decodeHtml(q.correct_answer),
          options: shuffledOptions,
          category: decodeHtml(q.category),
          difficulty: q.difficulty
        };
      });

      setQuestions(formattedQuestions);
      setCurrentQuestion(0);
      setScore(0);
      setSelectedAnswer(null);
      setAnswered(false);
      setAnsweredQuestions([]);
      setGameState(GAME_CONFIG.states.PLAYING);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // Fetch questions on mount
  useEffect(() => {
    fetchQuestions();
  }, []);

  const handleAnswer = (option) => {
    if (answered) return;

    setSelectedAnswer(option);
    setAnswered(true);

    const isCorrect = option === questions[currentQuestion].a;
    if (isCorrect) {
      setScore(prev => prev + 1);
    }

    setAnsweredQuestions([
      ...answeredQuestions,
      {
        question: questions[currentQuestion].q,
        selected: option,
        correct: questions[currentQuestion].a,
        isCorrect
      }
    ]);
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setAnswered(false);
    } else {
      // Quiz finished
      setGameState(GAME_CONFIG.states.WIN);
      if (score > highScore) {
        setHighScore(score);
      }
    }
  };

  const handleRestart = () => {
    setCurrentQuestion(0);
    setScore(0);
    setSelectedAnswer(null);
    setAnswered(false);
    setAnsweredQuestions([]);
    setGameState(GAME_CONFIG.states.PLAYING);
  };

  const handleReloadQuestions = () => {
    fetchQuestions();
  };

  const progress = questions.length > 0 ? ((currentQuestion + 1) / questions.length) * 100 : 0;

  return (
    <GameLayout
      title="Quiz Game ❓"
      score={score}
      highScore={highScore}
      gameState={gameState}
      onRestart={handleRestart}
      onHome={onHome}
    >
      <div style={styles.gameArea}>
        {/* Loading State */}
        {loading && (
          <div style={styles.loadingContainer}>
            <div style={styles.loadingSpinner}>⏳</div>
            <h2 style={{ color: GAME_CONFIG.colors.light }}>Loading Questions...</h2>
            <p style={{ color: GAME_CONFIG.colors.accent2 }}>Fetching questions from Open Trivia Database</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div style={styles.errorContainer}>
            <div style={styles.errorCard}>
              <h2 style={{ color: GAME_CONFIG.colors.danger }}>⚠️ Error</h2>
              <p style={{ color: GAME_CONFIG.colors.light, marginBottom: '1rem' }}>
                {error}
              </p>
              <button
                onClick={handleReloadQuestions}
                className="btn btn-primary"
              >
                🔄 Try Again
              </button>
            </div>
          </div>
        )}

        {/* Quiz Playing State */}
        {gameState === GAME_CONFIG.states.PLAYING && questions.length > 0 && !loading && !error && (
          <>
            {/* Progress Bar */}
            <div style={styles.progressContainer}>
              <div style={styles.progressLabel}>
                Question {currentQuestion + 1} of {questions.length}
              </div>
              <div style={styles.progressBar}>
                <div style={{ ...styles.progressFill, width: `${progress}%` }} />
              </div>
            </div>

            {/* Question Card */}
            <div style={styles.questionCard}>
              <div style={styles.categoryBadge}>
                {questions[currentQuestion].category}
              </div>
              <h2 style={styles.question}>
                {questions[currentQuestion].q}
              </h2>

              {/* Answer Options */}
              <div style={styles.optionsContainer}>
                {questions[currentQuestion].options.map((option, idx) => {
                  const isSelected = selectedAnswer === option;
                  const isCorrectAnswer = option === questions[currentQuestion].a;

                  let optionStyle = { ...styles.optionButton };

                  if (answered) {
                    if (isCorrectAnswer) {
                      optionStyle = { ...optionStyle, ...styles.optionCorrect };
                    } else if (isSelected && !isCorrectAnswer) {
                      optionStyle = { ...optionStyle, ...styles.optionIncorrect };
                    }
                  } else if (isSelected) {
                    optionStyle = { ...optionStyle, ...styles.optionSelected };
                  }

                  return (
                    <button
                      key={idx}
                      onClick={() => handleAnswer(option)}
                      disabled={answered}
                      style={optionStyle}
                      className="btn"
                    >
                      <span style={styles.optionLetter}>
                        {String.fromCharCode(65 + idx)}
                      </span>
                      <span>{option}</span>
                      {answered && isCorrectAnswer && <span style={styles.checkmark}>✓</span>}
                      {answered && isSelected && !isCorrectAnswer && <span style={styles.xmark}>✗</span>}
                    </button>
                  );
                })}
              </div>

              {/* Feedback Message */}
              {answered && (
                <div style={styles.feedbackContainer}>
                  {selectedAnswer === questions[currentQuestion].a ? (
                    <div style={styles.correctFeedback}>
                      🎉 Correct! Great job!
                    </div>
                  ) : (
                    <div style={styles.incorrectFeedback}>
                      ❌ Incorrect. The correct answer is: {questions[currentQuestion].a}
                    </div>
                  )}
                </div>
              )}

              {/* Next Button */}
              {answered && (
                <button
                  onClick={handleNext}
                  className="btn btn-primary mt-3"
                  style={styles.nextButton}
                >
                  {currentQuestion === questions.length - 1 ? '🏁 Finish' : 'Next Question →'}
                </button>
              )}
            </div>
          </>
        )}

        {gameState === GAME_CONFIG.states.WIN && (
          <div style={styles.finishScreen}>
            <div style={styles.finishCard}>
              <h1 style={styles.finishTitle}>Quiz Complete! 🎉</h1>

              <ScoreDisplay
                score={score}
                stats={{
                  'Final Score': `${score} / ${questions.length}`,
                  'Percentage': `${Math.round((score / questions.length) * 100)}%`,
                  'High Score': highScore,
                }}
              />

              <div style={styles.performanceMessage}>
                {score === questions.length && (
                  <div style={styles.perfectMessage}>Perfect Score! You're a quiz master! 👑</div>
                )}
                {score >= questions.length * 0.8 && score < questions.length && (
                  <div style={styles.excellentMessage}>Excellent performance! 🌟</div>
                )}
                {score >= questions.length * 0.6 && score < questions.length * 0.8 && (
                  <div style={styles.goodMessage}>Good job! Keep practicing! 💪</div>
                )}
                {score < questions.length * 0.6 && (
                  <div style={styles.tryAgainMessage}>Keep learning! Try again! 📚</div>
                )}
              </div>

              {/* Summary of Answers */}
              <div style={styles.summaryContainer}>
                <h3 style={styles.summaryTitle}>Answer Summary</h3>
                {answeredQuestions.map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      ...styles.summaryItem,
                      borderLeftColor: item.isCorrect ? GAME_CONFIG.colors.success : GAME_CONFIG.colors.danger,
                    }}
                  >
                    <div style={styles.summaryQuestion}>
                      <span style={styles.summaryNumber}>{idx + 1}.</span>
                      {item.question}
                    </div>
                    <div style={styles.summaryAnswer}>
                      Your answer: <strong>{item.selected}</strong>
                      {item.isCorrect ? (
                        <span style={{ color: GAME_CONFIG.colors.success, marginLeft: '0.5rem' }}>✓</span>
                      ) : (
                        <>
                          <span style={{ color: GAME_CONFIG.colors.danger, marginLeft: '0.5rem' }}>✗</span>
                          <div style={styles.correctAnswerNote}>Correct: {item.correct}</div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div style={styles.finishButtonsContainer}>
                <button
                  onClick={handleRestart}
                  className="btn btn-primary mt-4"
                  style={{ ...styles.restartButton, flex: 1 }}
                >
                  🔄 Try Again
                </button>
                <button
                  onClick={handleReloadQuestions}
                  className="btn btn-success mt-4"
                  style={{ ...styles.restartButton, flex: 1, marginLeft: '1rem' }}
                >
                  🔁 New Questions
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </GameLayout>
  );
};

const styles = {
  gameArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2rem',
    width: '100%',
    maxWidth: '800px',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1.5rem',
    padding: '3rem 2rem',
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.lg,
    minHeight: '300px',
  },
  loadingSpinner: {
    fontSize: '3rem',
    animation: 'spin 1s linear infinite',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    padding: '2rem 0',
  },
  errorCard: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.danger}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '2rem',
    maxWidth: '500px',
    textAlign: 'center',
  },
  progressContainer: {
    width: '100%',
    marginBottom: '1rem',
  },
  progressLabel: {
    color: GAME_CONFIG.colors.light,
    fontSize: '0.9rem',
    marginBottom: '0.5rem',
    fontWeight: 'bold',
  },
  progressBar: {
    width: '100%',
    height: '8px',
    backgroundColor: `rgba(0, 0, 0, 0.3)`,
    borderRadius: '4px',
    overflow: 'hidden',
    border: `1px solid ${GAME_CONFIG.colors.primary}`,
  },
  progressFill: {
    height: '100%',
    backgroundColor: GAME_CONFIG.colors.accent,
    transition: 'width 300ms ease-in-out',
  },
  questionCard: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '2rem',
    width: '100%',
  },
  categoryBadge: {
    display: 'inline-block',
    backgroundColor: GAME_CONFIG.colors.accent,
    color: '#000',
    padding: '0.4rem 0.8rem',
    borderRadius: '20px',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    marginBottom: '1rem',
    textTransform: 'uppercase',
  },
  question: {
    color: GAME_CONFIG.colors.light,
    fontSize: '1.5rem',
    marginBottom: '2rem',
    lineHeight: '1.4',
    fontWeight: 'bold',
  },
  optionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  optionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '1rem',
    backgroundColor: `rgba(102, 126, 234, 0.1)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.md,
    color: GAME_CONFIG.colors.light,
    textAlign: 'left',
    cursor: 'pointer',
    transition: `all ${GAME_CONFIG.transitions.normal}`,
    fontSize: '1rem',
    fontWeight: '500',
  },
  optionSelected: {
    backgroundColor: `rgba(102, 126, 234, 0.3)`,
    borderColor: GAME_CONFIG.colors.accent,
    boxShadow: `0 0 15px ${GAME_CONFIG.colors.accent}`,
  },
  optionCorrect: {
    backgroundColor: `rgba(72, 187, 120, 0.2)`,
    borderColor: GAME_CONFIG.colors.success,
    boxShadow: `0 0 15px ${GAME_CONFIG.colors.success}`,
  },
  optionIncorrect: {
    backgroundColor: `rgba(252, 129, 129, 0.2)`,
    borderColor: GAME_CONFIG.colors.danger,
    boxShadow: `0 0 15px ${GAME_CONFIG.colors.danger}`,
  },
  optionLetter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '2rem',
    height: '2rem',
    backgroundColor: GAME_CONFIG.colors.primary,
    borderRadius: '50%',
    fontWeight: 'bold',
    flexShrink: 0,
  },
  checkmark: {
    marginLeft: 'auto',
    color: GAME_CONFIG.colors.success,
    fontSize: '1.5rem',
  },
  xmark: {
    marginLeft: 'auto',
    color: GAME_CONFIG.colors.danger,
    fontSize: '1.5rem',
  },
  feedbackContainer: {
    marginTop: '1.5rem',
  },
  correctFeedback: {
    backgroundColor: `rgba(72, 187, 120, 0.15)`,
    border: `2px solid ${GAME_CONFIG.colors.success}`,
    borderRadius: GAME_CONFIG.radius.md,
    padding: '1rem',
    color: GAME_CONFIG.colors.success,
    textAlign: 'center',
    fontSize: '1rem',
    fontWeight: 'bold',
  },
  incorrectFeedback: {
    backgroundColor: `rgba(252, 129, 129, 0.15)`,
    border: `2px solid ${GAME_CONFIG.colors.danger}`,
    borderRadius: GAME_CONFIG.radius.md,
    padding: '1rem',
    color: GAME_CONFIG.colors.danger,
    textAlign: 'center',
    fontSize: '1rem',
    fontWeight: 'bold',
  },
  nextButton: {
    width: '100%',
    padding: '0.75rem 1rem',
    fontSize: '1rem',
    fontWeight: 'bold',
  },
  finishScreen: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  finishCard: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '2rem',
    width: '100%',
    maxWidth: '600px',
  },
  finishTitle: {
    color: GAME_CONFIG.colors.accent,
    textAlign: 'center',
    marginBottom: '1.5rem',
    fontSize: '2rem',
    fontWeight: 'bold',
  },
  performanceMessage: {
    marginTop: '1.5rem',
    marginBottom: '2rem',
  },
  perfectMessage: {
    backgroundColor: `rgba(72, 187, 120, 0.2)`,
    border: `2px solid ${GAME_CONFIG.colors.success}`,
    borderRadius: GAME_CONFIG.radius.md,
    padding: '1rem',
    color: GAME_CONFIG.colors.success,
    textAlign: 'center',
    fontSize: '1rem',
    fontWeight: 'bold',
  },
  excellentMessage: {
    backgroundColor: `rgba(0, 212, 255, 0.2)`,
    border: `2px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.md,
    padding: '1rem',
    color: GAME_CONFIG.colors.accent,
    textAlign: 'center',
    fontSize: '1rem',
    fontWeight: 'bold',
  },
  goodMessage: {
    backgroundColor: `rgba(237, 137, 54, 0.2)`,
    border: `2px solid #ed8936`,
    borderRadius: GAME_CONFIG.radius.md,
    padding: '1rem',
    color: '#fbb040',
    textAlign: 'center',
    fontSize: '1rem',
    fontWeight: 'bold',
  },
  tryAgainMessage: {
    backgroundColor: `rgba(252, 129, 129, 0.2)`,
    border: `2px solid ${GAME_CONFIG.colors.danger}`,
    borderRadius: GAME_CONFIG.radius.md,
    padding: '1rem',
    color: GAME_CONFIG.colors.danger,
    textAlign: 'center',
    fontSize: '1rem',
    fontWeight: 'bold',
  },
  summaryContainer: {
    marginTop: '2rem',
    width: '100%',
  },
  summaryTitle: {
    color: GAME_CONFIG.colors.light,
    marginBottom: '1rem',
    fontSize: '1.2rem',
    fontWeight: 'bold',
  },
  summaryItem: {
    backgroundColor: `rgba(0, 0, 0, 0.3)`,
    borderLeft: `4px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.md,
    padding: '1rem',
    marginBottom: '0.75rem',
  },
  summaryQuestion: {
    color: GAME_CONFIG.colors.light,
    marginBottom: '0.5rem',
    fontSize: '0.95rem',
  },
  summaryNumber: {
    color: GAME_CONFIG.colors.accent,
    fontWeight: 'bold',
    marginRight: '0.5rem',
  },
  summaryAnswer: {
    color: GAME_CONFIG.colors.accent2,
    fontSize: '0.9rem',
  },
  correctAnswerNote: {
    color: GAME_CONFIG.colors.success,
    fontSize: '0.85rem',
    marginTop: '0.25rem',
  },
  restartButton: {
    width: '100%',
    padding: '0.75rem 1rem',
    fontSize: '1rem',
    fontWeight: 'bold',
  },
  finishButtonsContainer: {
    display: 'flex',
    gap: '1rem',
    width: '100%',
    marginTop: '1rem',
  },
};

export default QuizGame;
